import {useState} from 'react';
import {useIntl, IntlShape} from 'react-intl';
import {useQuery} from 'react-query';
import {Carousel} from 'react-bootstrap';
import {Banner, BannerCategories, getBannersTiDisplay} from './api';

// Style par catégorie : libellé du badge + couleurs.
export const categoryMeta = (
  categoryName: string,
  intl: IntlShape
): {label: string; accent: string; soft: string; icon: string} => {
  switch (categoryName) {
    case BannerCategories.MARKETING_BANNER:
      return {label: intl.formatMessage({id: 'NEWS.TYPE.NEWS'}), accent: '#5da170', soft: '#eef6f0', icon: 'bi-megaphone-fill'};
    case BannerCategories.MAINTENANCE_BANNER:
      return {label: intl.formatMessage({id: 'NEWS.TYPE.MAINTENANCE'}), accent: '#5b70b0', soft: '#eef0f8', icon: 'bi-tools'};
    case BannerCategories.INCIDENT_BANNER:
      return {
        label: intl.formatMessage({id: 'NEWS.TYPE.INCIDENT'}),
        accent: '#de5050',
        soft: '#fbeded',
        icon: 'bi-exclamation-triangle-fill',
      };
    case BannerCategories.INFORMATION_BANNER:
      return {
        label: intl.formatMessage({id: 'NEWS.TYPE.INFO'}),
        accent: '#5b70b0',
        soft: '#eef0f8',
        icon: 'bi-info-circle-fill',
      };
    default:
      return {
        label: intl.formatMessage({id: 'NEWS.TYPE.INFO'}),
        accent: '#5b70b0',
        soft: '#eef0f8',
        icon: 'bi-info-circle-fill',
      };
  }
};

export const normalizeUrl = (news: string): string =>
  /^https?:\/\//.test(news) ? news : 'https://' + news;

const CompactBanner = ({title, description, news, category}: Banner): JSX.Element => {
  const intl = useIntl();
  const {label, accent, soft, icon} = categoryMeta(category.name, intl);
  return (
    <div className="card overflow-hidden">
      <div className="card-body d-flex align-items-center py-3 px-4">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
          style={{width: 40, height: 40, backgroundColor: soft}}
        >
          <i className={`bi ${icon} fs-5`} style={{color: accent}} />
        </div>

        <div className="ms-3 flex-grow-1 min-w-0">
          <span
            className="badge fw-semibold mb-1 text-capitalize"
            style={{backgroundColor: soft, color: accent}}
          >
            {label}
          </span>
          <div className="fw-semibold text-gray-900">{title}</div>
          {description && <div className="text-muted fs-7">{description}</div>}
        </div>

        {news && (
          <a
            href={normalizeUrl(news)}
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm text-white text-nowrap ms-3 flex-shrink-0"
            style={{backgroundColor: accent}}
          >
            {intl.formatMessage({id: 'COMMON.LEARN_MORE'})}
            <i className="bi bi-box-arrow-up-right ms-2" style={{color: '#fff'}} />
          </a>
        )}
      </div>
    </div>
  );
};

const CurrentlyCompact = (): JSX.Element | null => {
  const {data: banners} = useQuery('banners', getBannersTiDisplay);

  // Seuls les encarts s'affichent ici ; les popups sont gérés par NewsPopupModal.
  const encarts = (banners ?? []).filter((banner) => banner.type === 'encart');

  if (encarts.length === 0) {
    return null;
  }

  if (encarts.length === 1) {
    return <CompactBanner {...encarts[0]} />;
  }

  return <CompactCarousel banners={encarts} />;
};

const CompactCarousel = ({banners}: {banners: Banner[]}): JSX.Element => {
  const intl = useIntl();
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div>
      <Carousel
        controls={false}
        indicators={false}
        interval={10000}
        activeIndex={activeIndex}
        onSelect={setActiveIndex}
      >
        {banners.map((banner, index) => (
          <Carousel.Item key={index}>
            <CompactBanner {...banner} />
          </Carousel.Item>
        ))}
      </Carousel>

      {/* Puces de navigation visibles sur fond clair */}
      <div className="d-flex justify-content-center gap-2 mt-3">
        {banners.map((_, index) => (
          <button
            key={index}
            type="button"
            aria-label={intl.formatMessage({id: 'NEWS.BANNER_ALT'}, {index: index + 1})}
            onClick={() => setActiveIndex(index)}
            className="border-0 p-0 rounded-pill"
            style={{
              width: activeIndex === index ? 20 : 8,
              height: 8,
              backgroundColor: activeIndex === index ? '#5b70b0' : '#d0d4e0',
              transition: 'width 0.2s ease, background-color 0.2s ease',
              cursor: 'pointer',
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default CurrentlyCompact;
